import { ArticleShell } from './ArticleShell';
export function StressedEnough() {
    return (
        <ArticleShell>
            <h1 className="mb-20 text-mobile-display-2">
                Hvornår bliver stressen for meget?
            </h1>

            <p className="mb-20 text-body">
                Kender du det der med, at hverdagen bare kører derudad og du
                føler dig mere og mere drænet? Det kan være svært at sætte
                fingeren på præcis, hvornår stressen bliver for meget og vi har
                brug for at gøre noget anderledes. Vi er alle sammen
                forskellige, og det, der føles som meget pres for én, er måske
                helt normalt for en anden. Der er altså ikke nogen gylden grænse
                for hvornår man er for stresset. Det handler i højere grad om at
                lære at lytte til sig selv og fange de signaler, din krop og dit
                hoved sender dig.
            </p>

            <div className="my-20">
                <img
                    src="/images/articleIllustrations/StressedEnoughIllustration1.png"
                    alt="Stressed enough illustration"
                    className="w-full rounded-[10px]"
                />
            </div>

            <p className="mb-20 text-body">
                Vi har forsøgt at lave en oversigt over de adfærdsmæssige,
                kropslige og kognitive tegn, du kan holde øje med - hvis du er i
                tvivl om hvor stresset du er. Det skal ikke forstås som en
                tjekliste, hvor du skal have "fuld plade" for at have lov til at
                føle dig stresset. Men mere som nogle generelle tegn, som kan
                være gode at holde øje med. Måske kan du genkende dig selv i
                nogle af dem, eller måske har du svært ved at mærke efter. Det
                er helt okay!
            </p>

            <div className="rounded-[10px] bg-dark-red-10p p-20">
                <h2 className="mb-20 text-mobile-display-1">
                    Adfærdsmæssige tegn
                </h2>

                <p className="mb-20 text-body">
                    <span className="text-body-headline text-dark-red">
                        Søvnløshed:
                    </span>{' '}
                    Har du svært ved at falde i søvn, eller vågner du ofte om
                    natten?
                </p>
                <p className="mb-20 text-body">
                    <span className="text-body-headline text-dark-red">
                        Social tilbagetrækning:
                    </span>{' '}
                    Trækker du dig fra venner og familie, eller har du bare ikke
                    lyst til at være social?
                </p>
                <p className="mb-20 text-body">
                    <span className="text-body-headline text-dark-red">
                        Ubeslutsomhed:
                    </span>{' '}
                    Har du svært ved at træffe selv små beslutninger?
                </p>
                <p className="mb-20 text-body">
                    <span className="text-body-headline text-dark-red">
                        Rastløshed:
                    </span>{' '}
                    Føler du dig urolig og har svært ved at sidde stille?
                </p>
                <p className="mb-20 text-body">
                    <span className="text-body-headline text-dark-red">
                        Utålmodighed:
                    </span>{' '}
                    Bliver du hurtigt irriteret eller utålmodig med andre?
                </p>
                <p className="text-body">
                    <span className="text-body-headline text-dark-red">
                        Øget brug af stimulanser:
                    </span>{' '}
                    Bruger du mere kaffe, energidrikke eller andre ting for at
                    holde dig kørende?
                </p>
            </div>

            <div className="mt-20 rounded-[10px] bg-dark-red-10p p-20">
                <h2 className="mb-20 text-mobile-display-1">
                    Kroppens tegn – hvad din krop fortæller dig
                </h2>

                <p className="mb-20 text-body">
                    <span className="text-body-headline text-dark-red">
                        Hjertebanken:
                    </span>{' '}
                    Føler du, at dit hjerte banker hurtigt eller uregelmæssigt?
                </p>
                <p className="mb-20 text-body">
                    <span className="text-body-headline text-dark-red">
                        Brystsmerter:
                    </span>{' '}
                    Har du en trykkende fornemmelse i brystet?
                </p>
                <p className="mb-20 text-body">
                    <span className="text-body-headline text-dark-red">
                        Hovedpine:
                    </span>{' '}
                    Oplever du hyppig eller vedvarende hovedpine?
                </p>
                <p className="mb-20 text-body">
                    <span className="text-body-headline text-dark-red">
                        Svedeture:
                    </span>{' '}
                    Sveder du mere end normalt, selv når du ikke anstrenger dig?
                </p>
                <p className="mb-20 text-body">
                    <span className="text-body-headline text-dark-red">
                        Svimmelhed:
                    </span>{' '}
                    Føler du dig svimmel eller ustabil?
                </p>
                <p className="mb-20 text-body">
                    <span className="text-body-headline text-dark-red">
                        Mavesmerter/gener:
                    </span>{' '}
                    Har du ondt i maven, kvalme eller andre
                    fordøjelsesproblemer?
                </p>
                <p className="mb-20 text-body">
                    <span className="text-body-headline text-dark-red">
                        Hyperventilation:
                    </span>{' '}
                    Trækker du vejret hurtigt og overfladisk?
                </p>
                <p className="mb-20 text-body">
                    <span className="text-body-headline text-dark-red">
                        Øget/mindsket appetit:
                    </span>{' '}
                    Spiser du pludselig meget mere eller meget mindre end du
                    plejer?
                </p>
                <p className="mb-20 text-body">
                    <span className="text-body-headline text-dark-red">
                        Udmattethed:
                    </span>{' '}
                    Føler du dig konstant træt, selv efter at have sovet?
                </p>
                <p className="text-body">
                    <span className="text-body-headline text-dark-red">
                        Oplevet nedsat funktionalitet:
                    </span>{' '}
                    Har du svært ved at gøre de ting, du plejer at kunne?
                </p>
            </div>

            <div className="my-20 rounded-[10px] bg-dark-red-10p p-20">
                <h2 className="mb-20 text-mobile-display-1">
                    Kognitive tegn – hvad der sker i dit hoved
                </h2>

                <p className="mb-20 text-body">
                    <span className="text-body-headline text-dark-red">
                        Nedsat overblik:
                    </span>{' '}
                    Har du svært ved at se den store sammenhæng eller overskue
                    opgaver?
                </p>
                <p className="mb-20 text-body">
                    <span className="text-body-headline text-dark-red">
                        Nedsat planlægningsevne:
                    </span>{' '}
                    Er det svært at planlægge din dag eller uge?
                </p>
                <p className="mb-20 text-body">
                    <span className="text-body-headline text-dark-red">
                        Nedsat hukommelse:
                    </span>{' '}
                    Glemmer du ting nemmere end før?
                </p>
                <p className="mb-20 text-body">
                    <span className="text-body-headline text-dark-red">
                        Koncentrationsbesvær:
                    </span>{' '}
                    Har du svært ved at holde fokus på én ting ad gangen?
                </p>
                <p className="mb-20 text-body">
                    <span className="text-body-headline text-dark-red">
                        Taber tråden i en samtale:
                    </span>{' '}
                    Glemmer du, hvad du var i gang med at sige midt i en snak?
                </p>
                <p className="mb-20 text-body">
                    <span className="text-body-headline text-dark-red">
                        Indlæringsbesvær:
                    </span>{' '}
                    Har du svært ved at lære nye ting eller huske det, du lige
                    har læst?
                </p>
                <p className="text-body">
                    <span className="text-body-headline text-dark-red">
                        Svært ved at træffe beslutninger:
                    </span>{' '}
                    Føler du dig lammet, når du skal vælge noget?
                </p>
            </div>

            <p className="mb-20 text-body">
                Det er vigtigt at huske, at disse tegn er din krops måde at sige
                "hey, jeg har brug for en pause!" på. Hvis du genkender nogle af
                disse tegn, er det et signal om, at du måske skal lytte lidt
                ekstra til dig selv og overveje, om du har brug for at justere
                noget i din hverdag.
            </p>
        </ArticleShell>
    );
}
